import { AnalyticsDataPoint } from '../types/analytics.types';

export interface TimeGroupingRules {
  daily: { maxDays: number };
  weekly: { maxDays: number };
  monthly: { maxDays: number };
}

export interface TimeGroupedData {
  timeGrouping: 'daily' | 'weekly' | 'monthly';
  data: AnalyticsDataPoint[];
  xAxisFormat: string;
}

/**
 * Service for intelligent time grouping of chart data based on date ranges
 */
export class TimeGroupingService {

  private static readonly DEFAULT_RULES: TimeGroupingRules = {
    daily: { maxDays: 7 },
    weekly: { maxDays: 90 },
    monthly: { maxDays: 365 }
  };

  /**
   * Determine appropriate time grouping based on date range
   */
  static determineTimeGrouping(
    startDate: Date,
    endDate: Date,
    customRules?: TimeGroupingRules
  ): 'daily' | 'weekly' | 'monthly' {
    const rules = customRules || this.DEFAULT_RULES;
    const daysDiff = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));

    if (daysDiff <= rules.daily.maxDays) {
      return 'daily';
    } else if (daysDiff <= rules.weekly.maxDays) {
      return 'weekly';
    } else {
      return 'monthly';
    }
  }

  /**
   * Group and aggregate data by time periods and additional dimensions (like location)
   */
  static groupDataByTimeAndDimensions(
    data: AnalyticsDataPoint[],
    timeGrouping: 'daily' | 'weekly' | 'monthly',
    metrics: string[],
    aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count' = 'sum',
    additionalGroupBy?: string[] // e.g., ['location', 'region']
  ): AnalyticsDataPoint[] {
    const groupStart = performance.now();

    if (!data || data.length === 0) return [];

    // Map of compositeKey -> aggregated record (using single-pass accumulation)
    const grouped = new Map<string, any>();

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const rawDate = row.date;
      if (!rawDate) continue;

      // Fast path string date extraction (skips slow 'new Date()' creation for daily/monthly)
      let timeKey: string;
      if (typeof rawDate === 'string' && rawDate.length >= 10 && (timeGrouping === 'daily' || timeGrouping === 'monthly')) {
        timeKey = timeGrouping === 'daily' ? rawDate.substring(0, 10) : rawDate.substring(0, 7);
      } else {
        const date = typeof rawDate === 'string' ? this.parseLocalDateString(rawDate) : new Date(rawDate as any);
        if (!date || isNaN(date.getTime())) continue;
        timeKey = this.getTimeKey(date, timeGrouping);
      }

      let compositeKey = timeKey;
      let dimensionValues: string[] | null = null;
      if (additionalGroupBy && additionalGroupBy.length > 0) {
        dimensionValues = additionalGroupBy.map(dim => String(row[dim] || 'Unknown'));
        compositeKey = timeKey + '::' + dimensionValues.join('|');
      }

      let aggRow = grouped.get(compositeKey);

      if (!aggRow) {
        // Initialize new group
        aggRow = {
          timeKey,
          recordCount: 0,
          _metrics: {} as Record<string, { sum: number, count: number, min: number, max: number }>
        };

        // Setup initial metric states
        for (let j = 0; j < metrics.length; j++) {
          aggRow._metrics[metrics[j]] = { sum: 0, count: 0, min: Infinity, max: -Infinity };
        }

        // Add dimension values
        if (dimensionValues && additionalGroupBy) {
          for (let j = 0; j < additionalGroupBy.length; j++) {
            aggRow[additionalGroupBy[j]] = dimensionValues[j];
          }
        }

        grouped.set(compositeKey, aggRow);
      }

      // Update running aggregates continuously
      aggRow.recordCount++;

      for (let j = 0; j < metrics.length; j++) {
        const metric = metrics[j];
        const rawVal = row[metric];

        if (rawVal !== null && rawVal !== undefined) {
          const val = Number(rawVal);
          if (!isNaN(val)) {
            const mState = aggRow._metrics[metric];
            mState.sum += val;
            mState.count++;
            if (val < mState.min) mState.min = val;
            if (val > mState.max) mState.max = val;
          }
        }
      }
    }

    // Finalize metrics and push to result array
    const result: AnalyticsDataPoint[] = [];

    for (const aggRow of grouped.values()) {
      const parsedDate = this.parseTimeKey(aggRow.timeKey, timeGrouping);
      const finalDate = this.toLocalDateString(parsedDate);

      const finalRow: AnalyticsDataPoint = {
        date: finalDate, // Preserve generic date as string
        timeKey: aggRow.timeKey,
        recordCount: aggRow.recordCount
      };

      if (additionalGroupBy) {
        for (let j = 0; j < additionalGroupBy.length; j++) {
          finalRow[additionalGroupBy[j]] = aggRow[additionalGroupBy[j]];
        }
      }

      for (let j = 0; j < metrics.length; j++) {
        const metric = metrics[j];
        const mState = aggRow._metrics[metric];

        if (mState.count === 0) {
          finalRow[metric] = 0;
          continue;
        }

        switch (aggregation) {
          case 'sum': finalRow[metric] = mState.sum; break;
          case 'avg': finalRow[metric] = mState.sum / mState.count; break;
          case 'min': finalRow[metric] = mState.min; break;
          case 'max': finalRow[metric] = mState.max; break;
          case 'count': finalRow[metric] = mState.count; break;
        }
      }

      result.push(finalRow);
    }

    // Sort by date first, then by dimensions
    result.sort((a, b) => {
      // Much faster string comparison since they are already YYYY-MM-DD standardized
      const dateA = String(a.date);
      const dateB = String(b.date);
      if (dateA !== dateB) return dateA < dateB ? -1 : 1;

      if (additionalGroupBy && additionalGroupBy.length > 0) {
        const dimStrA = String(a[additionalGroupBy[0]] || '');
        const dimStrB = String(b[additionalGroupBy[0]] || '');
        return dimStrA.localeCompare(dimStrB);
      }

      return 0;
    });

    const groupEnd = performance.now();
    console.log(`⏱️ [PERF] Fast time grouping complete: ${data.length} → ${result.length} records in ${(groupEnd - groupStart).toFixed(2)}ms`);

    return result;
  }

  /**
   * Group and aggregate data by time periods (backward compatibility)
   */
  static groupDataByTime(
    data: AnalyticsDataPoint[],
    timeGrouping: 'daily' | 'weekly' | 'monthly',
    metrics: string[],
    aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count' = 'sum'
  ): AnalyticsDataPoint[] {
    return this.groupDataByTimeAndDimensions(data, timeGrouping, metrics, aggregation);
  }

  /**
   * Pivot multi-dimensional data for chart consumption
   * Converts rows like [{ date: '2024-01-01', location: 'Boston', sales: 100 }]
   * To chart format: [{ date: '2024-01-01', 'Boston': 100, 'Manhattan': 200 }]
   */
  static pivotDataForChart(
    data: AnalyticsDataPoint[],
    timeField: string = 'date',
    pivotDimension: string,
    valueFields: string[]
  ): AnalyticsDataPoint[] {
    if (!data || data.length === 0) return [];


    // Group by time first
    const timeGroups = new Map<string, AnalyticsDataPoint[]>();

    for (const row of data) {
      const timeValue = row[timeField];
      if (!timeValue) continue;

      const timeKey = typeof timeValue === 'string' ? timeValue : String(timeValue);

      if (!timeGroups.has(timeKey)) {
        timeGroups.set(timeKey, []);
      }
      timeGroups.get(timeKey)!.push(row);
    }

    // Pivot each time group
    const pivotedData: AnalyticsDataPoint[] = [];

    for (const [timeKey, timeRows] of Array.from(timeGroups.entries())) {
      const pivotRow: AnalyticsDataPoint = {
        [timeField]: timeKey
      };

      // Add each location's values as separate columns
      for (const row of timeRows) {
        const pivotValue = row[pivotDimension];
        if (pivotValue) {
          const locationKey = String(pivotValue);

          // Add each value field for this location
          for (const valueField of valueFields) {
            if (row[valueField] !== null && row[valueField] !== undefined) {
              pivotRow[locationKey] = row[valueField];
            }
          }
        }
      }

      pivotedData.push(pivotRow);
    }

    // Sort by time
    pivotedData.sort((a, b) => {
      const timeA = this.parseLocalDateString(String(a[timeField]));
      const timeB = this.parseLocalDateString(String(b[timeField]));
      return (timeA?.getTime() || 0) - (timeB?.getTime() || 0);
    });

    return pivotedData;
  }

  /**
   * Process chart data with intelligent time grouping and multi-dimensional support
   */
  static processChartDataWithDimensions(
    sourceData: AnalyticsDataPoint[],
    startDate: Date,
    endDate: Date,
    metrics: string[],
    aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count' = 'sum',
    additionalGroupBy?: string[], // e.g., ['location']
    customRules?: TimeGroupingRules
  ): TimeGroupedData {


    // Guard against invalid dates and set defaults if needed
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      console.warn(`⚠️ [TimeGrouping] Invalid date range provided, using defaults (today and 90 days ago):`, {
        providedStartDate: startDate.toString(),
        providedEndDate: endDate.toString(),
        startValid: !isNaN(startDate.getTime()),
        endValid: !isNaN(endDate.getTime())
      });

      // Set default dates: 90 days ago to today
      const today = new Date();
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(today.getDate() - 90);

      // Use defaults if invalid
      if (isNaN(startDate.getTime())) {
        startDate = ninetyDaysAgo;
      }
      if (isNaN(endDate.getTime())) {
        endDate = today;
      }

      console.log(`✅ [TimeGrouping] Using default date range:`, {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        daysDifference: Math.floor((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
      });
    }

    // Determine optimal time grouping
    const timeGrouping = this.determineTimeGrouping(startDate, endDate, customRules);

    // Group and aggregate data by time and additional dimensions
    let data = this.groupDataByTimeAndDimensions(sourceData, timeGrouping, metrics, aggregation, additionalGroupBy);

    // If we have additional groupBy dimensions (like location), pivot the data for chart consumption
    if (additionalGroupBy && additionalGroupBy.length > 0) {
      const pivotDimension = additionalGroupBy[0]; // Use first dimension (usually 'location')
      data = this.pivotDataForChart(data, 'date', pivotDimension, metrics);
    }

    // Add formatted date labels for better chart display
    data = this.generateXAxisLabels(data, timeGrouping);

    if (timeGrouping === 'weekly') {
      data = this.applyWeeklyAxisFilterBounds(data, startDate, endDate);
    }

    // Determine x-axis format
    const xAxisFormat = this.getXAxisFormat(timeGrouping);

    const uniqueLocations = additionalGroupBy && additionalGroupBy.length > 0
      ? Array.from(new Set(sourceData.map(d => d[additionalGroupBy[0]]).filter(Boolean)))
      : [];

    return {
      timeGrouping,
      data,
      xAxisFormat
    };
  }

  /**
   * Process chart data with intelligent time grouping (backward compatibility)
   */
  static processChartData(
    sourceData: AnalyticsDataPoint[],
    startDate: Date,
    endDate: Date,
    metrics: string[],
    aggregation: 'sum' | 'avg' | 'min' | 'max' | 'count' = 'sum',
    customRules?: TimeGroupingRules
  ): TimeGroupedData {
    return this.processChartDataWithDimensions(sourceData, startDate, endDate, metrics, aggregation, undefined, customRules);
  }

  /**
   * First/last weekly tick uses filter start/end when they fall inside that UTC Sun–Sat bucket;
   * middle ticks stay on the week’s Sunday (same convention as WTD).
   */
  private static applyWeeklyAxisFilterBounds(
    data: AnalyticsDataPoint[],
    startDate: Date,
    endDate: Date
  ): AnalyticsDataPoint[] {
    if (data.length === 0) return data;

    const startStr = this.toLocalDateString(startDate);
    const endStr = this.toLocalDateString(endDate);

    return data.map((row, idx) => {
      if (!row.date) return row;
      const weekStartSundayStr = String(row.date).split('T')[0];
      if (!/^\d{4}-\d{2}-\d{2}$/.test(weekStartSundayStr)) return row;

      const weekStartSunday = this.parseLocalDateString(weekStartSundayStr);
      const weekEndSaturdayStr = this.toLocalDateString(this.addUtcDays(weekStartSunday, 6));

      const isFirst = idx === 0;
      const isLast = idx === data.length - 1;
      let ymd = weekStartSundayStr;

      if (data.length === 1) {
        if (startStr >= weekStartSundayStr && startStr <= weekEndSaturdayStr) ymd = startStr;
        else if (endStr >= weekStartSundayStr && endStr <= weekEndSaturdayStr) ymd = endStr;
      } else {
        if (isFirst && startStr >= weekStartSundayStr && startStr <= weekEndSaturdayStr) ymd = startStr;
        if (isLast && endStr >= weekStartSundayStr && endStr <= weekEndSaturdayStr) ymd = endStr;
      }

      if (ymd === weekStartSundayStr) return row;

      const d = this.parseLocalDateString(ymd);
      return {
        ...row,
        date: ymd,
        timeKey: ymd,
        dateFormatted: this.formatDateForDisplay(d, 'weekly')
      };
    });
  }

  private static addUtcDays(d: Date, n: number): Date {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() + n, 0, 0, 0, 0));
  }

  /**
   * Generate time key for grouping
   * Uses UTC methods to match date filter and avoid timezone shifts
   */
  private static getTimeKey(date: Date, timeGrouping: 'daily' | 'weekly' | 'monthly'): string {
    switch (timeGrouping) {
      case 'daily':
        const timeKey = this.toLocalDateString(date);
        return timeKey;

      case 'weekly': {
        // UTC Sunday (week start) as YYYY-MM-DD — Sun–Sat weeks, aligned with WTD preset.
        const sunday = new Date(date);
        sunday.setUTCDate(date.getUTCDate() - date.getUTCDay());
        return this.toLocalDateString(sunday);
      }

      case 'monthly':
        return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;

      default:
        return date.toISOString().split('T')[0];
    }
  }

  /**
   * Parse time key back to date
   * Uses UTC for consistency with getTimeKey
   */
  private static parseTimeKey(timeKey: string, timeGrouping: 'daily' | 'weekly' | 'monthly'): Date {
    switch (timeGrouping) {
      case 'daily':
        return this.parseLocalDateString(timeKey);

      case 'weekly': {
        const base = timeKey.includes('T') ? timeKey.split('T')[0] : timeKey;
        // Current format: UTC Sunday (first day of Sun–Sat week)
        if (/^\d{4}-\d{2}-\d{2}$/.test(base)) {
          return this.parseLocalDateString(base);
        }
        const [year, weekStr] = timeKey.split('-W');
        const weekNum = parseInt(weekStr, 10);
        if (!isNaN(weekNum)) {
          const jan1 = new Date(Date.UTC(parseInt(year, 10), 0, 1, 0, 0, 0, 0));
          const daysToAdd = (weekNum - 1) * 7;
          return new Date(jan1.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
        }
        return this.parseLocalDateString(timeKey);
      }

      case 'monthly':
        // Parse YYYY-MM format - use UTC
        const [yearStr, monthStr] = timeKey.split('-');
        return new Date(Date.UTC(parseInt(yearStr), parseInt(monthStr) - 1, 1, 0, 0, 0, 0));

      default:
        return new Date(timeKey);
    }
  }

  /**
   * Get human-readable x-axis format based on time grouping
   */
  private static getXAxisFormat(timeGrouping: 'daily' | 'weekly' | 'monthly'): string {
    switch (timeGrouping) {
      case 'daily':
        return 'MMM dd';  // "Jan 15"
      case 'weekly':
        return 'MMM dd';  // "Jan 15" (week start date)
      case 'monthly':
        return 'MMM yyyy'; // "Jan 2024"
      default:
        return 'MMM dd';
    }
  }

  /**
   * Format date for display on x-axis
   * Uses timeZone: 'UTC' to match global date range filter - avoids off-by-one for users west of UTC
   */
  static formatDateForDisplay(date: Date, timeGrouping: 'daily' | 'weekly' | 'monthly'): string {
    const utcOptions: Intl.DateTimeFormatOptions = { timeZone: 'UTC' };

    switch (timeGrouping) {
      case 'daily':
        // Show "Jan 15" for daily - matches global date range filter
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...utcOptions });
      case 'weekly':
        // Show "Jan 15" (Sunday week start, UTC)
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...utcOptions });
      case 'monthly':
        // Show "Jan 2024" for monthly
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric', ...utcOptions });
      default:
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...utcOptions });
    }
  }

  /**
   * Generate formatted x-axis labels for chart consumption
   */
  static generateXAxisLabels(
    data: AnalyticsDataPoint[],
    timeGrouping: 'daily' | 'weekly' | 'monthly'
  ): AnalyticsDataPoint[] {
    return data.map(row => {
      if (row.date) {
        const date = typeof row.date === 'string' ? this.parseLocalDateString(row.date as string) : new Date(row.date as any);
        if (date && !isNaN(date.getTime())) {
          return {
            ...row,
            dateFormatted: this.formatDateForDisplay(date, timeGrouping),
            date: this.toLocalDateString(date)
          };
        }
      }
      return row;
    });
  }

  // Convert a Date to UTC YYYY-MM-DD (no timezone offset issues)
  private static toLocalDateString(d: Date): string {
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  // Parse a YYYY-MM-DD (or with time) string as UTC date (no timezone shift)
  private static parseLocalDateString(s: string): Date {
    const base = s.includes('T') ? s.split('T')[0] : s;
    const parts = base.split('-').map(Number);
    if (parts.length === 3 && parts.every(n => !isNaN(n))) {
      const [y, m, d] = parts;
      return new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));
    }
    return new Date(s);
  }
} 